import React, { useEffect, useState } from 'react';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import { WizardButtonGroup } from '../wizard/wizard-button-group-component';
import {
  GoADivider,
  GoADropdown,
  GoADropdownItem,
  GoAFormItem,
  GoARadioGroup,
  GoARadioItem,
  GoASpacer,
} from '@abgov/react-components';
import { set } from 'lodash';
import { updateCollision } from '../../state/slices/collisionSlice';
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';
import { NotificationType } from '../notifications/notification-component';
import { Collision } from '../../models/collision';

const roadClassMapping = [
  {
    label: 'One way street or roadway',
    eCollisionLabel: 'Undivided one-way',
    helperText: '',
    selectTravelingLane: true,
  },
  {
    label: 'Two way street or roadway',
    eCollisionLabel: 'Undivided two-way',
    helperText: 'traffic moves in both directions with no barrier or median',
    selectTravelingLane: true,
  },
  {
    label: 'Divided roadway, no barrier',
    eCollisionLabel: 'Divided no barrier',
    helperText:
      'road is divided by a painted line or median strip rather than a physical barrier, separating traffic',
    selectTravelingLane: true,
  },
  {
    label: 'Divided roadway, separated by physical structure or median',
    eCollisionLabel: 'Divided with barrier',
    helperText: '',
    selectTravelingLane: true,
  },
  {
    label: 'Unknown',
    eCollisionLabel: 'unknown',
    helperText: '',
    selectTravelingLane: false,
  },
];

const specialFacilityMapping = [
  {
    label: 'Interchange ramp',
    eCollisionLabel: 'Interchange ramp',
    showOther: false,
  },
  {
    label: 'Interchange loop',
    eCollisionLabel: 'Interchange loop',
    showOther: false,
  },
  {
    label: 'Service road',
    eCollisionLabel: 'Service road',
    helperText: '',
    showOther: false,
  },
  {
    label: 'Not applicable',
    eCollisionLabel: 'Not applicable',
    helperText: '',
    showOther: false,
  },
  {
    label: 'Other',
    eCollisionLabel: '',
    helperText: '',
    showOther: true,
  },
];

const otherSpecialFacilityMapping = [
  {
    label: 'Bridge/Overpass',
    eCollisionLabel: 'Bridge/Overpass',
  },
  {
    label: 'Tunnel/Underpass',
    eCollisionLabel: 'Tunnel/Underpass',
  },
  {
    label: 'Divided Highway Crossover',
    eCollisionLabel: 'Divided Highway Crossover',
  },
  {
    label: 'Traffic Circle',
    eCollisionLabel: 'Traffic Circle',
  },
];

interface RoadTypesComponentProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const RoadTypesComponent = ({
  navigationConfiguration,
}: RoadTypesComponentProps) => {
  const dispatch = useAppDispatch();

  const defaultSelection = 0;
  const specialFacilityOtherId = 99;

  const headerPrimaryRoadTypeErrorText = 'Make a selection below';
  const primaryRoadTypeErrorText = 'Choose a primary road type';

  const headerSecondaryRoadTypeErrorText =
    'You must choose one secondary road type';
  const secondaryRoadTypeErrorText = 'Choose a secondary road type';

  const headerTravelingLandErrorText =
    'You must choose a teaveling lane in primary road types';
  const travelingLaneErrorText = 'Choose a traveling lane';
  const chooseOtherRoadTypeErrorText = 'Choose other road type from the list';

  const [primaryFormError, setPrimaryFormError] = useState('');
  const [secondaryFormError, setSecondaryFormError] = useState('');
  const [travelingLaneFormError, setTravelingLaneFormError] = useState('');
  const [chooseOtherRoadTypeFormError, setChooseOtherRoadTypeFormError] =
    useState('');

  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  const [nextPage, setNextPage] = useState('');

  const [showSelectedTravelLane, setShowSelectedTravelLane] = useState(false);
  const [showOtherSpecialFacility, setShowOtherSpecialFacility] =
    useState(false);
  const [selectedRoadClass, setSelectedRoadClass] = useState(
    stateCollision.collision.roadClass
      ? stateCollision.collision.roadClass
      : defaultSelection,
  );
  const [selectedTravelingLane, setSelectedTravelingLane] = useState(
    stateCollision.collision.travelingLane
      ? stateCollision.collision.travelingLane
      : defaultSelection,
  );
  const [selectedSpecialFacility, setSelectedSpecialFacility] =
    useState(defaultSelection);

  const [selectedOtherSpecialFacility, setSelectedOtherSpecialFacility] =
    useState(defaultSelection);

  useEffect(() => {
    if (stateCollision.collision.specialFacility) {
      const foundSpecialFacility =
        stateConfiguration.configuration.specialFacilities.find(
          (x) => x.id === stateCollision.collision.specialFacility,
        );

      if (foundSpecialFacility) {
        const foundOtherSpecialFacilityMapping =
          otherSpecialFacilityMapping.find((x) => {
            return (
              x.eCollisionLabel.toLowerCase() ===
              foundSpecialFacility.value.toLowerCase()
            );
          });

        if (foundOtherSpecialFacilityMapping) {
          setShowOtherSpecialFacility(true);
          setSelectedSpecialFacility(specialFacilityOtherId);
          setSelectedOtherSpecialFacility(foundSpecialFacility.id);
        } else {
          setSelectedSpecialFacility(foundSpecialFacility.id);
        }
      }
    }
  }, [stateCollision.collision.specialFacility]);

  useEffect(() => {
    if (stateCollision.collision.roadClass) {
      setShowSelectedTravelLane(
        shouldShowSelectedTravelLane(
          stateCollision.collision.roadClass.toString(),
        ),
      );
    }
  }, [stateCollision.collision.roadClass]);

  useEffect(() => {
    const pageConfig = navigationConfiguration.pagingConfiguration.find(
      (item) => item.default !== undefined,
    );

    setNextPage(pageConfig?.default ?? 'error');
  }, []);

  const shouldShowSelectedTravelLane = (id: string) => {
    const roadClass = stateConfiguration.configuration.roadClasses.find(
      (x) => x.id == parseInt(id),
    );

    const foundRoadClassMapping = roadClassMapping.find(
      (mapping) =>
        mapping.eCollisionLabel.toLowerCase() ===
        roadClass?.value.toLowerCase(),
    );

    if (foundRoadClassMapping) return foundRoadClassMapping.selectTravelingLane;

    return false;
  };

  const validate = (): boolean => {
    dispatch(removeAllValidationErrorNotifications());
    let valid = true;

    if (
      selectedRoadClass === defaultSelection &&
      selectedSpecialFacility === defaultSelection
    ) {
      setPrimaryFormError(primaryFormError);
      setSecondaryFormError(secondaryFormError);
      dispatch(
        pushNotification({
          body: headerPrimaryRoadTypeErrorText,
          notificationType: NotificationType.importantValidationError,
        }),
      );
      dispatch(
        pushNotification({
          body: headerSecondaryRoadTypeErrorText,
          notificationType: NotificationType.importantValidationError,
        }),
      );
      valid = false;
    }

    if (valid && selectedRoadClass === defaultSelection) {
      setPrimaryFormError(primaryRoadTypeErrorText);
      dispatch(
        pushNotification({
          body: primaryRoadTypeErrorText,
          notificationType: NotificationType.importantValidationError,
        }),
      );
      valid = false;
    }

    if (valid && selectedSpecialFacility === defaultSelection) {
      setSecondaryFormError(secondaryRoadTypeErrorText);
      dispatch(
        pushNotification({
          body: secondaryRoadTypeErrorText,
          notificationType: NotificationType.importantValidationError,
        }),
      );
      valid = false;
    }

    if (
      shouldShowSelectedTravelLane(selectedRoadClass.toString()) &&
      selectedTravelingLane === defaultSelection
    ) {
      setTravelingLaneFormError(travelingLaneErrorText);
      dispatch(
        pushNotification({
          body: travelingLaneErrorText,
          notificationType: NotificationType.importantValidationError,
        }),
      );
      valid = false;
    }

    if (
      selectedSpecialFacility == specialFacilityOtherId &&
      selectedOtherSpecialFacility == defaultSelection
    ) {
      setChooseOtherRoadTypeFormError(chooseOtherRoadTypeErrorText);
      dispatch(
        pushNotification({
          body: chooseOtherRoadTypeErrorText,
          notificationType: NotificationType.importantValidationError,
        }),
      );
      valid = false;
    }

    return valid;
  };

  const save = () => {
    let updatedCollision: Collision = { ...stateCollision.collision };

    if (
      navigationConfiguration.updateMetadata &&
      navigationConfiguration.stepId
    ) {
      const stepUpdated = navigationConfiguration.updateMetadata(
        updatedCollision,
        navigationConfiguration.stepId!,
        navigationConfiguration.stageCompleted
          ? navigationConfiguration.stageCompleted
          : false,
        !navigationConfiguration.stageCompleted,
      );

      if (stepUpdated && updatedCollision.metadata) {
        updatedCollision.metadata = {
          ...updatedCollision.metadata,
          formState: stepUpdated,
        };
      }
    }

    updatedCollision.roadClass = selectedRoadClass;
    updatedCollision.travelingLane = selectedTravelingLane ?? undefined;
    updatedCollision.specialFacility =
      selectedSpecialFacility === specialFacilityOtherId
        ? selectedOtherSpecialFacility
        : selectedSpecialFacility;

    dispatch(updateCollision(updatedCollision));

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  return (
    <div>
      <h2>Road types</h2>
      <GoASpacer vSpacing={'l'} />
      Select the primary and secondary road types relevant to your collision
      situation. If you're unsure, select 'Unknown' or 'Not applicable' where
      appropriate.
      <GoASpacer vSpacing={'l'} />
      <div className="large-body">Primary road types</div>
      <GoASpacer vSpacing={'m'} />
      <GoAFormItem error={primaryFormError}>
        <GoARadioGroup
          name="primaryGroup"
          value={selectedRoadClass.toString()}
          error={primaryFormError.length > 0}
          onChange={(_, value) => {
            setPrimaryFormError('');
            dispatch(removeAllValidationErrorNotifications());
            setSelectedRoadClass(parseInt(value));

            setShowSelectedTravelLane(shouldShowSelectedTravelLane(value));
          }}
        >
          {roadClassMapping.map((roadClass) => {
            const id = stateConfiguration.configuration.roadClasses
              .find(
                (x) =>
                  x.value.toLowerCase() ===
                  roadClass.eCollisionLabel.toLowerCase(),
              )
              ?.id.toString();

            const control = (
              <GoARadioItem
                value={id}
                label={roadClass.label}
                description={
                  <span style={{ font: 'var(--goa-typography-body-xs)' }}>
                    {roadClass.helperText}
                  </span>
                }
                key={id}
              />
            );
            return control;
          })}
        </GoARadioGroup>
      </GoAFormItem>
      <div style={{ display: showSelectedTravelLane ? 'block' : 'none' }}>
        <GoASpacer vSpacing={'l'} />
        <GoAFormItem
          error={travelingLaneFormError}
          label="Choose a traveling lane from median"
        >
          <GoADropdown
            placeholder="--Select--"
            error={travelingLaneFormError.length > 0}
            onChange={(_, value) => {
              setTravelingLaneFormError('');
              dispatch(removeAllValidationErrorNotifications());
              setSelectedTravelingLane(parseInt(value.toString()));
              const roadClass =
                stateConfiguration.configuration.roadClasses.find(
                  (x) => x.id == parseInt(value.toString()),
                );

              const foundRoadClassMapping = roadClassMapping.find(
                (mapping) =>
                  mapping.eCollisionLabel.toLowerCase() ===
                  roadClass?.value.toLowerCase(),
              );
              if (foundRoadClassMapping)
                setShowSelectedTravelLane(
                  foundRoadClassMapping.selectTravelingLane,
                );
            }}
            value={selectedTravelingLane.toString()}
          >
            {stateConfiguration.configuration.travelingLane.map(
              (travelingLane) => {
                return (
                  <GoADropdownItem
                    label={travelingLane.value}
                    value={travelingLane.id.toString()}
                    key={travelingLane.id}
                  />
                );
              },
            )}
          </GoADropdown>
        </GoAFormItem>
      </div>
      <GoASpacer vSpacing={'xl'} />
      <GoADivider />
      <GoASpacer vSpacing={'xl'} />
      <div className="large-body">Secondary road types</div>
      <GoASpacer vSpacing={'2xs'} />
      Select a secondary road type (if applicable)
      <GoASpacer vSpacing={'l'} />
      <GoAFormItem error={secondaryFormError}>
        <GoARadioGroup
          name="secondaryGroup"
          value={selectedSpecialFacility.toString()}
          error={secondaryFormError.length > 0}
          onChange={(item, value) => {
            setSecondaryFormError('');
            dispatch(removeAllValidationErrorNotifications());
            setSelectedSpecialFacility(parseInt(value));

            setShowOtherSpecialFacility(
              value == specialFacilityOtherId.toString(),
            );
          }}
        >
          {specialFacilityMapping.map((specialFacility) => {
            let id = stateConfiguration.configuration.specialFacilities
              .find(
                (x) =>
                  x.value.toLowerCase() ===
                  specialFacility.eCollisionLabel.toLowerCase(),
              )
              ?.id.toString();

            if (specialFacility.showOther) {
              id = specialFacilityOtherId.toString();
            }

            const control = (
              <GoARadioItem
                value={id}
                label={specialFacility.label}
                description={
                  <span style={{ font: 'var(--goa-typography-body-xs)' }}>
                    {specialFacility.helperText}
                  </span>
                }
                key={id}
              />
            );
            return control;
          })}
        </GoARadioGroup>
      </GoAFormItem>
      <div style={{ display: showOtherSpecialFacility ? 'block' : 'none' }}>
        <GoASpacer vSpacing={'l'} />

        <GoAFormItem
          label="Choose other road type"
          error={chooseOtherRoadTypeFormError}
        >
          <GoADropdown
            value={selectedOtherSpecialFacility.toString()}
            onChange={(_, item) => {
              setChooseOtherRoadTypeFormError('');
              dispatch(removeAllValidationErrorNotifications());
              setSelectedOtherSpecialFacility(parseInt(item.toString()));
            }}
            error={chooseOtherRoadTypeFormError.length > 0}
          >
            {otherSpecialFacilityMapping.map((specialFacility) => {
              let id = stateConfiguration.configuration.specialFacilities
                .find(
                  (x) =>
                    x.value.toLowerCase() ===
                    specialFacility.eCollisionLabel.toLowerCase(),
                )
                ?.id.toString();

              const control = (
                <GoADropdownItem
                  value={id!}
                  label={specialFacility.label}
                  key={id}
                />
              );
              return control;
            })}
          </GoADropdown>
        </GoAFormItem>
      </div>
      <WizardButtonGroup
        nextPage={nextPage}
        validate={validate}
        goToReview={navigationConfiguration.goToReview}
        save={save}
        navigate={navigationConfiguration.navigate}
      />
    </div>
  );
};
